import { useEffect, useState } from 'react';
import { Clock, Trash } from 'lucide-react';
import { Edit, MoreHoriz } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { Switch } from 'components/ui/switch';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { getApiErrorDetail } from 'utils/api-error';
import { usePauseScheduledJob, useResumeScheduledJob } from '../hooks/use-scheduled-jobs';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';
import { describeScheduleSpec } from '../utils/scheduled-job-schedule';

type ScheduledJobRowProps = {
  job: ScheduledJobItem;
  onEdit: (job: ScheduledJobItem) => void;
  onDelete: (job: ScheduledJobItem) => void;
  isDeleting?: boolean;
  /** Agent settings drawer: no icon, badge or menu; schedule as the subtitle, toggle at the right edge. */
  compact?: boolean;
};

/**
 * One job on the scheduler page: title with the agent's name as a badge, an
 * active/paused toggle, and a menu with Edit and Delete. Clicking the row opens
 * the edit dialog.
 */
export function ScheduledJobRow({
  job,
  onEdit,
  onDelete,
  isDeleting = false,
  compact = false,
}: Readonly<ScheduledJobRowProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const pauseMutation = usePauseScheduledJob();
  const resumeMutation = useResumeScheduledJob();

  // The list refetches after a toggle; hold the new status until it arrives so
  // the switch does not flick back in between.
  const [status, setStatus] = useState(job.status);
  useEffect(() => {
    setStatus(job.status);
  }, [job.status]);

  // Only an active or paused job can be switched; a finished or failed one cannot.
  const canToggle = status === 'active' || status === 'paused';
  const isToggling = pauseMutation.isPending || resumeMutation.isPending;

  const handleToggle = async (active: boolean) => {
    try {
      if (active) {
        await resumeMutation.mutateAsync(job.id);
        setStatus('active');
      } else {
        await pauseMutation.mutateAsync(job.id);
        setStatus('paused');
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: active
          ? t('FAILED_TO_RESUME_JOB', { defaultValue: 'Failed to resume job' })
          : t('FAILED_TO_PAUSE_JOB', { defaultValue: 'Failed to pause job' }),
        description: getApiErrorDetail(error) ?? undefined,
      });
    }
  };

  // The compact row shows when the job runs; the scheduler page shows its prompt.
  let subtitle = job.prompt || job.description;
  if (compact && status === 'paused') {
    subtitle = t('PAUSED', { defaultValue: 'Paused' });
  } else if (compact && job.schedule) {
    subtitle = describeScheduleSpec(job.schedule);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'group relative flex w-full cursor-pointer items-start gap-3 rounded-lg border border-transparent text-left transition-colors',
        compact ? 'pr-14' : 'p-3 pr-24 hover:bg-muted/50'
      )}
      onClick={() => onEdit(job)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit(job);
        }
      }}
    >
      {compact ? null : (
        <span className="inline-flex size-5 shrink-0 overflow-hidden">
          <Clock
            className="size-5 text-grey-500 dark:text-muted-foreground"
            strokeWidth={1.5}
            aria-hidden
          />
        </span>
      )}

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          compact ? 'gap-1 leading-body' : 'gap-2 leading-heading'
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'truncate text-grey-900 dark:text-foreground',
              compact ? 'text-sm font-medium leading-body' : 'text-base font-semibold'
            )}
          >
            {job.name}
          </span>
          {job.agentName && !compact ? (
            <Badge
              variant="secondary"
              className="max-w-[40%] shrink-0 border-0 bg-foreground font-medium text-background hover:bg-foreground hover:text-background"
              title={job.agentName}
            >
              <span className="truncate">{job.agentName}</span>
            </Badge>
          ) : null}
        </div>
        {subtitle ? (
          <span
            className={cn(
              'truncate text-sm text-grey-500 dark:text-muted-foreground',
              compact ? 'font-medium leading-body' : 'font-normal'
            )}
          >
            {subtitle}
          </span>
        ) : null}
      </div>

      <div
        className={cn(
          'absolute top-1/2 flex -translate-y-1/2 items-center gap-3',
          compact ? 'right-0' : 'right-3'
        )}
      >
        {canToggle ? (
          <Switch
            checked={status === 'active'}
            disabled={isToggling}
            aria-label={t('SCHEDULED_JOB_ACTIVE_TOGGLE', { defaultValue: 'Job is active' })}
            onClick={(event) => event.stopPropagation()}
            onCheckedChange={(checked) => void handleToggle(checked)}
          />
        ) : null}

        {/* The drawer has no menu; clicking the row still opens the edit dialog. */}
        {compact ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('MORE_OPTIONS', { defaultValue: 'More options' })}
                className="inline-flex size-5 items-center justify-center text-grey-500 opacity-0 transition-opacity hover:text-grey-900 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 data-[state=open]:opacity-100 dark:text-muted-foreground dark:hover:text-foreground"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHoriz width={20} height={20} strokeWidth={1.5} aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-44"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem onSelect={() => onEdit(job)}>
                <Edit width={16} height={16} strokeWidth={1.5} aria-hidden />
                {t('EDIT', { defaultValue: 'Edit' })}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={dropdownMenuItemDestructiveClass}
                disabled={isDeleting}
                onSelect={(event) => {
                  event.preventDefault();
                  onDelete(job);
                }}
              >
                <Trash className="h-4 w-4" />
                {t('DELETE', { defaultValue: 'Delete' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
